import { React, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  getSchoolAssessmentsByDateRange,
} from "../services/APIClient";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SencoResultsClassOverview from "../components/SencoResultsClassOverview";
import { Container, Row, Col, Card } from "react-bootstrap";
import "../components/ClassOverview.css";
import useAcademicYearOptions from "../utils/useAcademicYearOptions";

export const getSencoAcademicYearDateRange = (academicYear) => ({
  startDate: academicYear.start.toISOString(),
  endDate: academicYear.end.toISOString(),
});

function SencoResultDetail({ asOfDate }) {
  const location = useLocation();
  let schoolid = location?.state?.schoolid;
  let teacherid = location?.state?.teacherid;
  let name = location.state.name.name;
  let teachers = location.state.teachers.teachers;
  let gradeLevel = location.state?.gradeLevel?.gradeLevel;

  const [allClassesAssessments, setAllClassesAssessments] = useState([]);
  const [gradeLevel1Assessments, setGradeLevel1Assessments] = useState([]);
  const [gradeLevel2Assessments, setGradeLevel2Assessments] = useState([]);
  const [gradeLevel3Assessments, setGradeLevel3Assessments] = useState([]);
  const [classIds, setClassIds] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const academicYearOptions = useAcademicYearOptions({ asOfDate, previousYears: 2 });
  const selectedAcademicYear = academicYearOptions.find(
    ({ display }) => display === selectedYear
  );
  const previousCurrentYear = useRef(academicYearOptions[0]?.display);
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)")?.matches
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const currentYear = academicYearOptions[0]?.display;
    if (selectedYear === previousCurrentYear.current && currentYear) {
      setSelectedYear(currentYear);
    }
    previousCurrentYear.current = currentYear;
  }, [academicYearOptions, selectedYear]);

  useEffect(() => {
    if (!schoolid || !selectedYear || !selectedAcademicYear) return;

    setIsLoading(true);

    // Use the new optimized API endpoint
    const { startDate, endDate } = getSencoAcademicYearDateRange(selectedAcademicYear);
    getSchoolAssessmentsByDateRange(schoolid, startDate, endDate)
      .then((response) => {
        // Ensure we're working with an array
        const data = Array.isArray(response) ? response : [];
        
        // Debug log to see the structure
        console.log("API Response:", data);
        
        // Set the main data
        setAllClassesAssessments(data);
        
        // Extract all classIds
        const ids = data.map(item => item.classId).filter(Boolean);
        setClassIds([...new Set(ids)]); // Use Set to deduplicate
        
        // Filter for grade level 1
        const gradeLevel1 = data.filter(item => 
          Object.values(item.assessments || {}).some(a => a?.grade_level1)
        );
        
        // Filter for grade level 2
        const gradeLevel2 = data.filter(item => 
          Object.values(item.assessments || {}).some(a => a?.grade_level2)
        );
        
         // Filter for grade level 2
        const gradeLevel3 = data.filter(item => 
          Object.values(item.assessments || {}).some(a => a?.grade_level3)
        );
        
        setGradeLevel1Assessments(gradeLevel1);
        setGradeLevel2Assessments(gradeLevel2);
        setGradeLevel3Assessments(gradeLevel3);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching assessments:", error);
        // Initialize with empty arrays on error
        setAllClassesAssessments([]);
        setGradeLevel1Assessments([]);
        setGradeLevel2Assessments([]);
        setGradeLevel3Assessments([]);
        setClassIds([]);
        setIsLoading(false);
      });
  }, [schoolid, selectedYear, selectedAcademicYear]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window?.innerWidth < window?.innerHeight);
    };

    window?.addEventListener("resize", handleOrientationChange);

    return () => {
      window?.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    window?.HubSpotConversations?.widget?.refresh();
  }, []);

  return (
    <Container fluid className="vh-100">
      <HeadingBanner
        name={name}
        isLoggedIn={true}
        cloud={true}
        teacherid={teacherid}
      />
      <Row className="h-100">
        <LeftPanel />
        <Col sm={{ span: 10, offset: 2 }} className="blue-background">
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
                <p>Loading data, please wait...</p>
              </div>
            </div>
          ) : !selectedYear ? (
            <Card className="dynamic-box">
              <div className="school-info-header mb-3">
                <h4 className="mb-1">{name}</h4>
              </div>
              <h5 className="step-indicator">Step 1: Select Academic Year</h5>
              <Row className="justify-content-center mb-3">
                {academicYearOptions.map((year) => (
                  <Col md={4} key={year.storage} className="mb-3">
                    <button
                      className={`year-selection-button w-100 py-3 shadow-sm rounded ${
                        selectedYear === year.display ? "selected" : ""
                      }`}
                      onClick={() => setSelectedYear(year.display)}
                    >
                      <h3>{year.display}</h3>
                      <small className="text-muted d-block mt-2">
                        {year.start.toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric", timeZone: "Europe/Dublin" }
                        )}{" "}
                        -
                        {year.end.toLocaleDateString(
                          "en-IE",
                          { month: "short", year: "numeric", timeZone: "Europe/Dublin" }
                        )}
                      </small>
                    </button>
                  </Col>
                ))}
              </Row>
            </Card>
          ) : (
            <>
              <div className="current-selections p-2 mb-3 bg-light rounded mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{name}</h5>
                    <p className="mb-0 text-muted">
                      Academic Year: {selectedYear}
                    </p>
                  </div>
                  <button
                    variant="secondary"
                    className="blue-action-button"
                    onClick={() => setSelectedYear(null)}
                  >
                    Change Year
                  </button>
                </div>
              </div>
              <SencoResultsClassOverview
                teachers={teachers}
                gradeLevel={gradeLevel}
                name={name}
                teacherid={teacherid}
                schoolid={schoolid}
                allClassesAssessments={allClassesAssessments}
                classIds={classIds}
                gradeLevel1Assessments={gradeLevel1Assessments}
                gradeLevel2Assessments={gradeLevel2Assessments}
                gradeLevel3Assessments={gradeLevel3Assessments}
                academicYear={selectedAcademicYear}
              />
            </>
          )}
        </Col>
      </Row>
      {isPortrait && <OrientationPrompt />}
    </Container>
  );
}

export default SencoResultDetail;
